import { useEffect, useRef } from 'react'
import { Capacitor, type PluginListenerHandle } from '@capacitor/core'
import { App as CapacitorApp } from '@capacitor/app'
import { useBackStack } from '../context/BackStackContext'

export interface UseNativeBackButtonOptions {
  onEmpty: () => void
  disabled?: boolean
}

/**
 * Installs a hardware/gesture back-button listener for Capacitor Android.
 * Pops the topmost handler from BackStackContext. If the stack is empty,
 * invokes the `onEmpty` fallback callback (e.g. switch to primary tab or show exit sheet).
 */
export const useNativeBackButton = ({
  onEmpty,
  disabled = false,
}: UseNativeBackButtonOptions) => {
  const { pop } = useBackStack()
  const onEmptyRef = useRef(onEmpty)

  useEffect(() => {
    onEmptyRef.current = onEmpty
  }, [onEmpty])

  useEffect(() => {
    if (disabled) return
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') return

    let listenerHandle: PluginListenerHandle | null = null
    let active = true

    void CapacitorApp.addListener('backButton', () => {
      const handled = pop()
      if (!handled) {
        onEmptyRef.current()
      }
    }).then((handle) => {
      if (!active) {
        void handle.remove()
      } else {
        listenerHandle = handle
      }
    })

    return () => {
      active = false
      void listenerHandle?.remove()
    }
  }, [disabled, pop])
}
